import React, { useReducer, useContext, useRef, useCallback, useEffect, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { modifierGroupsReducer } from './modifierGroupsReducer';
import { ModifierGroupsActionTypes } from './modifierGroupsActions';
import {
	modifierGroupsInitialState,
	ModifierGroupsContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './modifierGroupsContext';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedModifierGroups, deleteModifierGroups } from 'backend/modifierGroups';
import { AxiosResponse } from 'axios';
import { IGetPaginatedModifierGroups, IModifierGroups } from 'types';

const ModifierGroupsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(modifierGroupsReducer, modifierGroupsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IModifierGroups>, IGetPaginatedModifierGroups>(
		isMounted.current ? getPaginatedModifierGroups : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ModifierGroupsActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					modifiersGroups: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteModifierGroups(state.IDForDeletion);
			const filteredModifierGroups = state.modifiersGroups.modifierGroups.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: ModifierGroupsActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					modifiersGroups: {
						modifierGroupsCount: state.modifiersGroups.modifierGroupsCount - 1,
						modifierGroups: filteredModifierGroups,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: ModifierGroupsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.modifiersGroups]);

	const changeSearchString = useCallback(
		(value: string) => {
			if (allowChangingSearchString) {
				dispatch({
					type: ModifierGroupsActionTypes.CHANGE_SEARCH_STRING,
					payload: {
						searchString: value,
					},
				});
			}
		},
		[allowChangingSearchString],
	);

	const setIDSelectedCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModifierGroupsActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
			},
		});
	}, []);

	const setIDSelectedRestaurant = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModifierGroupsActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
			},
		});
	}, []);

	const resetSelectedControls = useCallback(
		() => dispatch({ type: ModifierGroupsActionTypes.RESET_SELECTED_CONTROLS }),
		[],
	);

	const setSortBy = useCallback((sortBy: TSortBy) => {
		dispatch({
			type: ModifierGroupsActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	}, []);

	const setCountOfItems = useCallback((count: TCountOfItems) => {
		dispatch({
			type: ModifierGroupsActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	}, []);

	const changeCurrentPage = useCallback((page: number) => {
		dispatch({
			type: ModifierGroupsActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	}, []);

	const setIDForDeletion = useCallback((value: string) => {
		dispatch({
			type: ModifierGroupsActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ModifierGroupsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setFiltersFromQueryString = useCallback(
		(value: IFilterValues) => {
			dispatch({
				type: ModifierGroupsActionTypes.SET_FILTERS_FROM_QUERY_STRING,
				payload: {
					filters: value,
				},
			});
			isMounted.current = true;
			refetch();
		},
		[refetch, isMounted],
	);

	return (
		<ModifierGroupsContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDSelectedCustomer,
				setIDSelectedRestaurant,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				modifiersGroups: state.modifiersGroups,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ModifierGroupsContext.Provider>
	);
};

export default ModifierGroupsState;
